import { z } from "zod";

/** Website sources (the founder, 2026-10-16; the knowledge sources round, 2026-10-22: "read everything that fits, flag forums, say it
 *  plainly"; DECISIONS "2026-10-22 — Knowledge sources: read everything, flag forums, say it plainly"). One source kind "Website", no mode
 *  anywhere: when a website source is added, and again once a month, the worker checks it once. A site is cut into sections, the site's own
 *  top folders below the source's address in the order the sitemap first shows them; every section is read (stored and counted against the
 *  words cap) except a section the check flags as a forum, which is searched live at draft time while its live search is on (the owner may
 *  switch it off to store it, never the other way). A whole forum is one flagged section, the source's own address; a site that answers only
 *  challenges is `unreadable` and searched as a whole. The decision lives in `kb_sources.config.site`; its reason is kept there for the team
 *  and never answered. This file holds the decision's shape, the founder's numbers and the section rules both sides read; the words the
 *  Sources page shows are `websiteWords.ts`. */
export const SITE_KINDS = ["website", "forum"] as const;
export type SiteKind = (typeof SITE_KINDS)[number];
/** a website before its first decision (the founder, 2026-10-16: "Analysing" with the kit's spinner): a value of the API's read only, never stored */
export const SITE_ANALYSING = "analysing" as const;
/** how the API reads a decided website: read (some or all of it stored), or searched as a whole (a whole forum searched live, an unreadable site) */
export type SiteReadTreatment = "read" | "searched" | typeof SITE_ANALYSING;
export const SITE_DECIDED_BY = ["signature", "model", "size", "unreadable"] as const;
export type SiteDecidedBy = (typeof SITE_DECIDED_BY)[number];

/** one section of a site as the decision keeps it: its path from the host's root ("/docs/"), whether the check flagged it a forum, and
 *  `live`: a forum part searched when needed (the send-and-sections round, 2026-10-23: only a forum part is ever searched; the one-card round's
 *  per-section "skip" went, leaving out is the source's "Leave out" list, `excludedPaths`). `public: false` when the check's samples in the
 *  section could not be read (401/403, a challenge, refused by robots.txt). Strict: migration 0103 stripped yesterday's `skip`. */
export const SiteSectionSchema = z.object({ path: z.string().regex(/^\//), forum: z.boolean(), live: z.boolean(), public: z.boolean().optional() }).strict();
/** why "Search when needed" cannot be chosen for a section: its samples were not public, the search index does not know the site, or the
 *  check had no live search to ask */
export const SEARCH_WHY = ["not_public", "not_indexed", "no_search"] as const;
export type SearchWhy = (typeof SEARCH_WHY)[number];
export type SiteSection = z.infer<typeof SiteSectionSchema>;
/** the worker's decision as `kb_sources.config.site` keeps it; `reason` is the model's sentence (null when no model decided), for the team
 *  only; `documentation` names the sections the check labelled documentation, read first (decision 2 of 2026-10-22) */
export const SiteDecisionSchema = z.object({
  kind: z.enum(SITE_KINDS), unreadable: z.boolean(), sections: z.array(SiteSectionSchema), documentation: z.array(z.string().regex(/^\//)).optional(),
  /** the one-card round: whether the live search's index knows the site (one `site:<host>` search at the check, a result means known); null:
   *  the check had no live search */
  indexed: z.boolean().nullable().optional(),
  reason: z.string().nullable(), decidedBy: z.enum(SITE_DECIDED_BY), signature: z.string().nullable(), decidedAt: z.string().datetime({ offset: true }),
}).strict();
export type SiteDecision = z.infer<typeof SiteDecisionSchema>;
/** why a section was not read in the last read: the plan's words ("cap": an upgrade reads it) or the crawler's limits of a run ("limit":
 *  2,000 pages, 20 MB, 20 minutes; no upgrade helps) */
export const UNREAD_WHY = ["cap", "limit"] as const;
export type UnreadWhy = (typeof UNREAD_WHY)[number];
/** the last read's sections it did not finish, kept beside the decision in `kb_sources.config.unread` */
export const UnreadSectionSchema = z.object({ path: z.string().regex(/^\//), why: z.enum(UNREAD_WHY) }).strict();
export type UnreadSection = z.infer<typeof UnreadSectionSchema>;

/** a section as the API answers it: the decision's section with its state and the stored pages' counts (a section counts as read only when
 *  all of its pages are stored) */
export type SiteSectionState = "read" | "live" | "not_read";
export type SiteSectionRead = SiteSection & { state: SiteSectionState; why: UnreadWhy | null; pages: number; words: number; /** the one-card round, for the forum parts */ searchable?: boolean; searchWhy?: SearchWhy | null };
/** why the section may not be searched when needed (null: it may): a forum always may; any other section only when its samples were public
 *  and the search index knows the site */
export function searchWhyOf(site: Pick<SiteDecision, "indexed">, s: Pick<SiteSection, "forum" | "public">): SearchWhy | null {
  if (s.forum) return null;
  if (s.public === false) return "not_public";
  if (site.indexed === true) return null;
  return site.indexed === false ? "not_indexed" : "no_search";
}

/** the founder's numbers: a site the crawler's limits allow is read up to them (2026-10-22: no site is searched for its size any more) */
export const SITE_SIZE_BOUND = 2_000;
/** the pages looked at to decide */
export const SITE_SAMPLES = 5;
/** the decision is made again once it is this old (the monthly re-check) */
export const SITE_RECHECK_MS = 30 * 86_400_000;
/** live search: a workspace's searches in a rolling hour, how long an answer is kept, and how many queries one draft may search */
export const SEARCHES_PER_HOUR = 60;
export const SEARCH_CACHE_MS = 24 * 3_600_000;
export const SEARCH_QUERIES_PER_DRAFT = 2;
/** what the stored draft says when the hour's searches are used up (the red team pinned the words "60 searches an hour") */
export const SEARCH_LIMIT_NOTE = `Live search skipped: this workspace used its ${SEARCHES_PER_HOUR} searches an hour.`;

/** the decision is due: none yet, or the last one is a month old */
export const siteDecisionDue = (site: Pick<SiteDecision, "decidedAt"> | null | undefined, now: Date): boolean => !site || now.getTime() - Date.parse(site.decidedAt) >= SITE_RECHECK_MS;

/* ---------- sections: the site's own top folders ---------- */

/** the source's own path as a section path, with its slash ("https://x.test/support" → "/support/") */
export function basePath(location: string): string {
  let p = "/";
  try { p = new URL(location).pathname || "/"; } catch { p = location.startsWith("/") ? location : "/"; }
  return p.endsWith("/") ? p : `${p}/`;
}
/** The section a page belongs to: the first folder below the source's path ("/docs/", "/changelog/"); a page right at the source's path, or a
 *  file directly in it ("/about"), belongs to the source's own section. Null for a page outside the source. */
export function sectionOf(base: string, url: string): string | null {
  let path: string;
  try { path = new URL(url).pathname; } catch { return null; }
  if (path === base.slice(0, -1) || path === base) return base;
  if (!path.startsWith(base)) return null;
  const rest = path.slice(base.length);
  const cut = rest.indexOf("/");
  return cut > 0 ? `${base}${rest.slice(0, cut)}/` : base;
}
/** the sections of a list of pages, in the order the list first shows them */
export function sectionsOf(base: string, urls: readonly string[]): string[] {
  const out = new Set<string>();
  for (const u of urls) { const s = sectionOf(base, u); if (s) out.add(s); }
  return [...out];
}
/** a section is searched live now: a forum part whose live search is on (the send-and-sections round: a part that is not a forum is read,
 *  even one yesterday's choice stored as searched) */
export const isLiveSection = (s: Pick<SiteSection, "live" | "forum">): boolean => s.live && s.forum;
/** a section keeps no page: a forum part searched when needed */
export const keepsNoPages = isLiveSection;
/** the whole source is searched instead of read: a site that answers only challenges, or a whole forum whose live search is on */
export const searchedWhole = (site: Pick<SiteDecision, "kind" | "unreadable" | "sections">): boolean =>
  site.unreadable || (site.kind === "forum" && site.sections.length > 0 && site.sections.every(isLiveSection));

const DOCS_NAMES = /^(docs?|documentation|help|support|guides?|manuals?|kb)$/i;
/** a section the read takes first (decision 2 of 2026-10-22): one the check labelled documentation, or one named docs, help, support,
 *  guide, manual or kb */
export const isDocsSection = (path: string, documentation: readonly string[] = []): boolean =>
  documentation.includes(path) || DOCS_NAMES.test(path.split("/").filter(Boolean).at(-1) ?? "");
/** The order the read takes the sections in: documentation first, then the rest in the check's order, a forum the owner switched to stored
 *  last (it never pushes out what was read); sections searched live and those the "Leave out" list covers whole are left out. */
export function readOrder(site: Pick<SiteDecision, "sections" | "documentation">, excludedPaths: readonly string[] = []): string[] {
  const stored = site.sections.filter((s) => !keepsNoPages(s) && !isLeftOut(s.path, excludedPaths));
  const docs = stored.filter((s) => !s.forum && isDocsSection(s.path, site.documentation));
  const rest = stored.filter((s) => !s.forum && !docs.includes(s));
  return [...docs, ...rest, ...stored.filter((s) => s.forum)].map((s) => s.path);
}

/* ---------- "Leave out" (the send-and-sections round, 2026-10-23) ---------- */

/** the "Leave out" list's bounds: entries and each entry's length */
export const EXCLUDED_PATHS_LIMITS = Object.freeze({ entries: 100, length: 300 });
/** One source's "Leave out" list, as `kb_sources.config.excludedPaths` keeps it and the route takes it: paths from the site's root, never "/"
 *  alone (that empties the source: remove it instead), at most 100 of 300 characters. */
export const ExcludedPathsSchema = z.array(z.string().trim().min(2).max(EXCLUDED_PATHS_LIMITS.length).regex(/^\/[^\s]*$/, "A path from the site's root, starting with /").refine((p) => p.replace(/\/+$/, "") !== "", { message: "Leaving out / would leave out the whole site; remove the source instead" }))
  .max(EXCLUDED_PATHS_LIMITS.entries);
/** a path's segments in lower case ("/Blog/2026/" → ["blog", "2026"]); a full address or a query is judged by its path */
function segmentsOf(pathOrUrl: string): string[] {
  let path = pathOrUrl;
  if (!pathOrUrl.startsWith("/")) { try { path = new URL(pathOrUrl).pathname; } catch { path = pathOrUrl; } }
  return path.split(/[?#]/)[0]!.toLowerCase().split("/").filter(Boolean);
}
/** A page is left out when an entry of the list is a prefix of its path on whole segments, ignoring letter case and a trailing slash:
 *  "/blog" leaves out "/blog/2026/v2/" and "/BLOG/x", never "/blogroll/". */
export function isLeftOut(pathOrUrl: string, excludedPaths: readonly string[]): boolean {
  if (!excludedPaths.length) return false;
  const page = segmentsOf(pathOrUrl);
  return excludedPaths.some((entry) => { const e = segmentsOf(entry); return e.length > 0 && e.length <= page.length && e.every((x, i) => x === page[i]); });
}
/** the source's "Leave out" list from `kb_sources.config` (an unreadable list reads as none) */
export function excludedPathsOf(config: unknown): string[] {
  const p = ExcludedPathsSchema.safeParse((config as { excludedPaths?: unknown } | null | undefined)?.excludedPaths);
  return p.success ? p.data : [];
}
